import { API_TYPES } from "@src/appV2/api";
import { remove } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const documentStatusEnum = z.enum(["APPROVED", "PENDING", "REJECTED"]);

const documentAttributesSchema = z.object({
  hcpId: z.string(),
  requirementId: z.string(),
  uploadedForRequirementId: z.string(),
  status: documentStatusEnum,
  archived: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  fileStorageRootFolder: z.string(),
  fileStorageFileKey: z.string(),
  subType: z.string().optional(),
  name: z.string(),
  displayName: z.string().optional(),
});

const archiveDocumentResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(API_TYPES.documents),
    attributes: documentAttributesSchema,
  }),
});

export type ArchiveDocumentResponse = z.infer<typeof archiveDocumentResponseSchema>;

interface ArchiveDocumentRequest {
  id: string;
}

export function useArchiveDocument(
  options: UseMutationOptions<ArchiveDocumentResponse, AxiosError, ArchiveDocumentRequest> = {}
): UseMutationResult<ArchiveDocumentResponse, AxiosError, ArchiveDocumentRequest> {
  return useMutation({
    mutationFn: async (request: ArchiveDocumentRequest) => {
      const response = await remove({
        url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/documents/${request.id}`,
        responseSchema: archiveDocumentResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ARCHIVE_DOCUMENT_FAILURE,
    },
    ...options,
  });
}
